import {Box, DialogContent, DialogTitle, Divider, Typography} from "@mui/material";
import {Alert} from "@mui/lab";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "../../../../base/button/button";
import {ReactComponent as SuccessSvg} from "../../../../../../assets/images/art-like/quick-order-submitted.svg";
import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import SummaryInfoCard from "../../../../app-specific/summary-cards/summary-info-card";
import {exportCSVFile} from "../../../../../../core/services/utils/utils";

const MAX_VISIBLE_FAILURE_MESSAGES = 5;

/**
 * Builds a deduplicated failure summary for add or remove adjustment batches.
 * @param {Array<{isAdd: boolean, errorMessage?: string|null, chunk?: Array}>} failedResults
 * @param {boolean} isAdd
 */
const getFailureSummary = (failedResults, isAdd) => {
    const failures = (failedResults ?? []).filter(r => r.isAdd === isAdd);
    if (!failures.length) {
        return null;
    }
    return {
        batchCount: failures.length,
        recordCount: failures.reduce((n, r) => n + (r.chunk?.length ?? 0), 0),
        messages: [...new Set(
            failures
                .map(r => r.errorMessage?.trim())
                .filter(Boolean)
        )],
    };
};

const AdjustmentFailureAlert = ({summary, titleKey, t}) => {
    if (!summary) {
        return null;
    }

    const visibleMessages = summary.messages.slice(0, MAX_VISIBLE_FAILURE_MESSAGES);
    const hiddenMessageCount = summary.messages.length - visibleMessages.length;

    return (
        <Alert severity="error" variant="filled" sx={{width: '100%'}}>
            <AlertTitle>
                {t(titleKey, {
                    batchCount: summary.batchCount,
                    recordCount: summary.recordCount,
                })}
            </AlertTitle>
            {
                visibleMessages.length === 0 &&
                <Typography variant="body2">{t('unknownError')}</Typography>
            }
            {
                visibleMessages.length === 1 &&
                <Typography variant="body2">{visibleMessages[0]}</Typography>
            }
            {
                visibleMessages.length > 1 &&
                <Box component="ul" sx={{m: 0, pl: 2.5}}>
                    {
                        visibleMessages.map(message => (
                            <Typography key={message} component="li" variant="body2">
                                {message}
                            </Typography>
                        ))
                    }
                </Box>
            }
            {
                hiddenMessageCount > 0 &&
                <Typography variant="body2" sx={{mt: visibleMessages.length ? 1 : 0}}>
                    {t('moreErrorsHint', {count: hiddenMessageCount})}
                </Typography>
            }
        </Alert>
    );
};

const ConfirmationSection = ({state, reviewState, resetState, fields}) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.import-adjustment-dialog.confirmation'});

    const addFailureSummary = useMemo(
        () => getFailureSummary(state.failedResults, true),
        [state.failedResults],
    );

    const removeFailureSummary = useMemo(
        () => getFailureSummary(state.failedResults, false),
        [state.failedResults],
    );

    const summaryCard = useMemo(() => ({
        header: {
            title: t('importSummary'),
            value: "",
            loadingValue: t('loading'),
        },
        body: [
            {
                id: 'invalidEntries',
                title: t('invalidEntries'),
                value: reviewState.invalidEntries?.length ?? 0,
            },
            {
                id: 'totalAdjustments',
                title: t('totalAdjustments'),
                value: state.successfulResults.length + state.failedResults.length,
            },
            {
                id: 'successfulAdjustments',
                title: t('successfulAdjustments'),
                value: state.successfulResults.length,
            },
            {
                id: 'failedAdjustments',
                title: t('failedAdjustments'),
                value: state.failedResults.length,
            },
            {
                id: 'successfulAdjustmentRecords',
                title: t('successfulAdjustmentRecords'),
                value: state.successfulResults.reduce((p, c) => p + c?.chunk?.length ?? 0, 0),
            },
            {
                id: 'failedAdjustmentRecords',
                title: t('failedAdjustmentRecords'),
                value: state.failedResults.reduce((p, c) => p + c?.chunk?.length ?? 0, 0),
            },
        ],
    }), [reviewState.invalidEntries?.length, state.failedResults, state.successfulResults, t])

    const hasAnyInvalids = useMemo(() => {
        return !!reviewState.invalidEntries?.length;
    }, [reviewState.invalidEntries?.length])

    const hasAnyErrors = useMemo(() => {
        return !!state.failedResults?.length;
    }, [state.failedResults?.length])

    const hasAnyComplete = useMemo(() => {
        return !!state.successfulResults?.length
    }, [state.successfulResults?.length])


    /**
     * Downloads the InvalidEntries of the import process.
     * @type {(function())}
     */
    const downloadInvalidEntries = useCallback(() => {
        const entries = reviewState.invalidEntries;
        const keyTitleMapping = {
            ...fields.reduce((p, c) => {
                p[c.name] = c.title;
                return p;
            }, {}),
            errorMessage: t('errorMessageColumn'),
        }
        const data = entries.map(e =>
            Object.fromEntries(
                Object.entries(e)
                    .filter(([key]) => keyTitleMapping[key])
                    .map(([key, value]) => [keyTitleMapping[key], value])
            )
        )
        exportCSVFile(data, t('invalidEntriesFileName'))
    }, [fields, reviewState.invalidEntries, t])

    /**
     * Downloads the SuccessfulRecords of the import process.
     * @type {(function())}
     */
    const downloadSuccessfulRecords = useCallback(() => {
        const entries = state.successfulResults.reduce((p, c) => {
            return [...p, ...c.chunk.map(e => ({
                ...e,
                quantity: c.isAdd ? e.quantity : -e.quantity,
            }))];
        }, []);
        const keyTitleMapping = {
            ...fields.reduce((p, c) => {
                p[c.name] = c.title;
                return p;
            }, {}),
        }
        const data = entries.map(e =>
            Object.fromEntries(
                Object.entries(e)
                    .filter(([key]) => keyTitleMapping[key])
                    .map(([key, value]) => [keyTitleMapping[key], value])
            )
        )
        exportCSVFile(data, t('successfulRecordsFileName'))
    }, [fields, state.successfulResults, t])

    /**
     * Downloads the FailedRecords of the import process.
     * @type {(function())}
     */
    const downloadFailedRecords = useCallback(() => {
        const entries = state.failedResults.reduce((p, c) => {
            return [...p, ...c.chunk.map(e => ({
                ...e,
                quantity: c.isAdd ? e.quantity : -e.quantity,
                errorMessage: c.errorMessage ?? '',
            }))];
        }, []);
        const keyTitleMapping = {
            ...fields.reduce((p, c) => {
                p[c.name] = c.title;
                return p;
            }, {}),
            errorMessage: t('errorMessageColumn'),
        }
        const data = entries.map(e =>
            Object.fromEntries(
                Object.entries(e)
                    .filter(([key]) => keyTitleMapping[key])
                    .map(([key, value]) => [keyTitleMapping[key], value])
            )
        )
        exportCSVFile(data, t('failedRecordsFileName'))
    }, [fields, state.failedResults, t])

    /**
     * Downloads the Report of the import process.
     * @type {(function())}
     */
    const downloadReport = useCallback(() => {
        const entries = state.successfulResults.map(e => e.data);
        const keyTitleMapping = Object.fromEntries(
            Object
                .keys(entries[0] ?? {})
                .map(e => [
                    e,
                    e.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
                ])
        )
        const data = entries.map(e =>
            Object.fromEntries(
                Object.entries(e)
                    .filter(([key]) => keyTitleMapping[key])
                    .map(([key, value]) => [keyTitleMapping[key], value])
            )
        )
        exportCSVFile(data, t('successfulReportFileName'))
    }, [state.successfulResults, t])

    return (
        <>
            <DialogTitle id={'scroll-dialog-title'} component={'div'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    {t('title')}
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent className={'p-3 d-flex flex-column align-items-center w-100'} sx={{gap: 1}}>
                <SuccessSvg width={'80%'}/>
                <Typography className={'text-center'}>
                    {hasAnyErrors ? t('completedWithErrors') : t('completedSuccessfully')}
                </Typography>
                {
                    addFailureSummary &&
                    <AdjustmentFailureAlert
                        summary={addFailureSummary}
                        titleKey="addFailedTitle"
                        t={t}
                    />
                }
                {
                    removeFailureSummary &&
                    <AdjustmentFailureAlert
                        summary={removeFailureSummary}
                        titleKey="removeFailedTitle"
                        t={t}
                    />
                }
                <SummaryInfoCard
                    header={summaryCard.header}
                    body={summaryCard.body}
                />
                <div className={'d-flex flex-column w-100'} style={{gap: 12}}>
                    {
                        hasAnyInvalids &&
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            onClick={downloadInvalidEntries}
                            size={'medium'}
                            fullWidth
                        >
                            {t('downloadInvalidEntries')}
                        </Button>
                    }
                    {
                        hasAnyComplete &&
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            onClick={downloadSuccessfulRecords}
                            size={'medium'}
                            fullWidth
                        >
                            {t('downloadSuccessfulRecords')}
                        </Button>
                    }
                    {
                        hasAnyErrors &&
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            onClick={downloadFailedRecords}
                            size={'medium'}
                            fullWidth
                        >
                            {t('downloadFailedRecords')}
                        </Button>
                    }
                    {
                        hasAnyComplete &&
                        <Button
                            variant={'contained'}
                            color={'primary'}
                            onClick={downloadReport}
                            size={'medium'}
                            fullWidth
                        >
                            {t('downloadSuccessfulReport')}
                        </Button>
                    }
                    <Button
                        variant={'text'}
                        color={'primary'}
                        size={'medium'}
                        onClick={resetState}
                    >
                        {t('importOtherAdjustments')}
                    </Button>
                </div>
            </DialogContent>
        </>
    );
}

export default ConfirmationSection;
